import { useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { GeoJSON, Marker, Pane, Polyline, Tooltip } from "react-leaflet";
import bezierSpline from "@turf/bezier-spline";
import { lineString } from "@turf/helpers";
import { divIcon, DomEvent } from "leaflet";
import hash from "object-hash";

import { Card, Stack, Typography } from "@mui/material";

import { useAppDispatch, useAppSelector } from "Hooks/useRedux";

import { Box } from "@mui/system";
import { STATIONS2 } from "Constants/Stations";
import zIndex from "@mui/material/styles/zIndex";
import DayPicker from "Components/DayPicker";
import { changeShowPaths, changeShowReportRightControl } from "Redux/sideSlice";
import useTrafficPath from "Hooks/useTrafficPath";

const ICON_SIZE = 22;

export const METRO_STATIONS_COLOR = {
  bus: "#6909B4",
  metro: "#35C602",
  taxi: "#FFAD00",
  train: "#F01818",
  bicycle: "#4754CA",
  walking: "#1DAEEF",
  behamrah: "#00C19F",
};

const Colors = ["#ec1c26", "#27388d", "#04a9f2", "#fddf17", "#099146", "#f26cab", "#833e9d"];

export default function TrafficPathArcAndShadow() {
  const { data } = useTrafficPath();

  const { visible, pathShowType, selectedPath } = useAppSelector((state) => state.trafficPath);
  const { pathLayer } = useAppSelector((state) => state.layers);

  const dispatch = useAppDispatch();

  const curvePolyline = useMemo(() => {
    if (!data) return null;

    let finalData;

    if (selectedPath.length > 0) {
      finalData = selectedPath;
    } else {
      finalData = data;
    }

    return finalData?.map((p: any) => {
      let path;
      if (selectedPath.length > 0) {
        path = p;
      } else {
        if (!p.Routes) return null;
        path = JSON.parse(`{${p.Routes}}`);
      }

      if (!path || path.stations.length === 0) return null;

      const index = Number(path.code) - 1;
      const { stations } = path;
      const sourceStation = stations[0];
      const destinationStation = stations[stations.length - 1];

      console.log("fbdfbdng", sourceStation, destinationStation);

      const stationData = STATIONS2.filter((i) => i.type === "metro")[0];
      const sourceIconMarkup = renderToStaticMarkup(
        <Box
          style={{
            width: "40px",
            height: "40px",
            backgroundColor: "#fff",
            borderRadius: "50%",
            border: "2px solid",
            display: "grid",
            placeItems: "center",
            borderColor: Colors[index],
          }}
        >
          {stationData.icon}
        </Box>
      );

      const destinationIconMarkup = renderToStaticMarkup(
        <Box
          style={{
            width: "49px",
            height: "53px",
            backgroundColor: "#ffffff00",
            borderRadius: "none",
            display: "grid",
            placeItems: "center",
            borderColor: Colors[index],
            marginLeft: "2px",
            marginTop: "2px",
          }}
        >
          <svg width="55" height="50" viewBox="4 2 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7.96891 4.24999C9.31606 1.91666 12.6839 1.91666 14.0311 4.25L18.3612 11.75C19.7084 14.0833 18.0244 17 15.3301 17H6.66986C3.97557 17 2.29163 14.0833 3.63878 11.75L7.96891 4.24999Z"
              fill="#fff"
              stroke={Colors[index]}
              strokeWidth="1"
              strokeLinejoin="round"
            />
          </svg>
          {stationData.icon}
        </Box>
      );

      const sourceIcon = divIcon({
        html: sourceIconMarkup,
        iconSize: [ICON_SIZE, ICON_SIZE],
        iconAnchor: [18, 30],
      });
      const destinationIcon = divIcon({
        html: destinationIconMarkup,
        iconSize: [ICON_SIZE, ICON_SIZE],
        iconAnchor: [18, 30],
      });

      const finalResult = [];

      const latLng1 = [Number(sourceStation.lat), Number(sourceStation.lng)];
      const latLng2 = [Number(destinationStation.lat), Number(destinationStation.lng)];

      const offsetX = latLng2[1] - latLng1[1];
      const offsetY = latLng2[0] - latLng1[0];

      const r1 = Math.sqrt(offsetX ** 2 + offsetY ** 2);
      const theta = Math.atan2(offsetY, offsetX);

      const thetaOffset = 3.14 / 20;

      const r2 = r1 / 2 / Math.cos(thetaOffset);
      const theta2 = theta + thetaOffset;

      const midpointX = r2 * Math.cos(theta2) + latLng1[1];
      const midpointY = r2 * Math.sin(theta2) + latLng1[0];

      const midpointLatLng = [midpointY, midpointX];

      finalResult.push(latLng1, midpointLatLng, latLng2);

      const line = lineString(finalResult.map((latLng) => [latLng[1], latLng[0]]));
      const curved = bezierSpline(line, { sharpness: 1, resolution: 10000 });

      const events = {
        click: () => {
          dispatch(changeShowReportRightControl(true));
          dispatch(changeShowPaths(false));
        },
      };

      const tooltipContent = (
        <Tooltip direction="top" offset={[0, -20]}>
          <Card sx={{ py: 2, px: 3, direction: "ltr", width: "300px" }} className="fa-number">
            <Stack alignItems="center">
              <Typography>{path.name}</Typography>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography sx={{ m: "0 !important" }}>نوع مسیر:</Typography>
                <Typography sx={{ m: "0 !important" }}>{stationData.icon}</Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography sx={{ m: "0 !important" }}>تعداد ایستگاه:</Typography>
                <Typography sx={{ m: "0 !important" }}>{stations.length}</Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%", mt: "10px" }}>
                <Typography sx={{ m: "0 !important" }}>کد مسیر</Typography>
                <Typography sx={{ m: "0 !important" }}>{path.code}</Typography>
              </Stack>
            </Stack>
          </Card>
        </Tooltip>
      );

      const sourceTooltip = (
        <Tooltip direction="top" key={`${sourceStation.id}`} offset={[0, -20]}>
          <Card sx={{ py: 2, px: 3, direction: "ltr", width: "300px" }}>
            <Stack alignItems="center">
              <Typography>{sourceStation.title}</Typography>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  کد ایستگاه :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {sourceStation.code}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  عرض جغرافیایی :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {sourceStation.lat}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  طول جغرافیایی :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {sourceStation.lng}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Tooltip>
      );

      const destinationTooltip = (
        <Tooltip direction="top" offset={[0, -20]}>
          <Card sx={{ py: 2, px: 3, direction: "ltr", width: "300px" }}>
            <Stack alignItems="center">
              <Typography>{destinationStation.title}</Typography>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  کد ایستگاه :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {destinationStation.code}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  عرض جغرافیایی :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {destinationStation.lat}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  طول جغرافیایی :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {destinationStation.lng}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Tooltip>
      );

      return (
        <>
          {(pathShowType.includes(3) || !((pathShowType.includes(1) || pathShowType.includes(5)) && visible)) && (
            <>
              <Marker
                key={`source-${path.id}`}
                position={[Number(sourceStation.lat), Number(sourceStation.lng)]}
                icon={sourceIcon}
                zIndexOffset={1000}
              >
                {sourceTooltip}
              </Marker>
              <Marker
                key={`destination-${path.id}`}
                position={[destinationStation.lat, destinationStation.lng]}
                icon={destinationIcon}
                zIndexOffset={1000}
              >
                {destinationTooltip}
              </Marker>
            </>
          )}

          {pathShowType.includes(4) && (
            <Polyline
              positions={[
                [Number(sourceStation.lat), Number(sourceStation.lng)],
                [Number(destinationStation.lat), Number(destinationStation.lng)],
              ]}
              pathOptions={{ opacity: 0.2, color: "#000", weight: 5 }}
              eventHandlers={events}
            >
              {tooltipContent}
            </Polyline>
          )}
          {pathShowType.includes(3) && (
            <GeoJSON data={curved} pathOptions={{ weight: 5, color: Colors[index] }} eventHandlers={events} key={hash(curved)}>
              {tooltipContent}
            </GeoJSON>
          )}
        </>
      );
    });
  }, [data, pathShowType, visible, selectedPath]);

  return (
    <>
      <Pane name="path-arc" style={{ zIndex: pathLayer }} key={pathLayer}>
        {curvePolyline}
      </Pane>

      {/* {simplePolyline} */}
    </>
  );
}
